/** @jsxImportSource preact */
import { render, h } from 'preact';
import { ChatWidget } from './ChatWidget';
import css from './styles.css';

interface BootOpts {
  site: string;
  erpUrl?: string;
  greeting?: string;
  onlineHours?: string;
  user?: { id?: string; name?: string; phone?: string };
  /** 마운트 대상. 기본 = document.body. */
  mountTo?: HTMLElement;
}

const ROOT_ID = 'allsteel-chat-root';
const STYLE_ID = 'allsteel-chat-style';

let booted = false;

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = css;
  document.head.appendChild(style);
}

function ensureRoot(mountTo?: HTMLElement): HTMLElement {
  let root = document.getElementById(ROOT_ID);
  if (!root) {
    root = document.createElement('div');
    root.id = ROOT_ID;
    (mountTo || document.body).appendChild(root);
  }
  return root;
}

/** 명시 부팅 — `AllSteelChat.boot({ site: 'allsteelshop' })` */
export function boot(opts: BootOpts) {
  if (booted) {
    console.warn('[AllSteelChat] already booted');
    return;
  }
  booted = true;
  injectStyles();
  const root = ensureRoot(opts.mountTo);
  const erpUrl = opts.erpUrl || autoErpUrl();
  render(
    h(ChatWidget, {
      site: opts.site,
      erpUrl,
      greeting: opts.greeting,
      onlineHours: opts.onlineHours,
      user: opts.user,
    }),
    root
  );
}

/** <script data-site="..." data-erp-url="..."> 자동 부팅 */
function autoBoot() {
  if (booted) return;
  if (typeof document === 'undefined') return;
  const scripts = document.querySelectorAll('script[data-site]');
  for (const s of Array.from(scripts) as HTMLScriptElement[]) {
    const site = s.getAttribute('data-site');
    if (!site) continue;
    boot({
      site,
      erpUrl: s.getAttribute('data-erp-url') || undefined,
      greeting: s.getAttribute('data-greeting') || undefined,
      onlineHours: s.getAttribute('data-online-hours') || undefined,
    });
    break;
  }
}

function autoErpUrl(): string {
  // 1) window.AllSteelChatErpUrl override
  const w = window as any;
  if (w && w.AllSteelChatErpUrl) return String(w.AllSteelChatErpUrl);
  // 2) script src 의 origin (chat.umd.js 가 ERP 서버에서 호스팅됨)
  const cur = document.currentScript as HTMLScriptElement | null;
  if (cur && cur.src) {
    try {
      const u = new URL(cur.src);
      return u.origin;
    } catch (_) { /* ignore */ }
  }
  // 3) production 기본
  return 'https://erp.allsteelshop.com';
}

// 글로벌 노출 (UMD/IIFE — globalName=AllSteelChat)
(window as any).AllSteelChat = { boot };

// DOMContentLoaded 후 자동 부팅
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoBoot);
} else {
  autoBoot();
}
